import clsx from 'clsx';
import React from 'react';
import { withStyles } from '@lifeomic/chroma-react/styles';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import {
  CheckCircle,
  PlusCircle,
  ChevronDown,
  Settings,
} from '@lifeomic/chromicons';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl';
import { push } from 'connected-react-router';
import _ from 'lodash';
import { hasAccessToProject } from '../../util/abac';
import EnforceABAC, {
  permissions,
} from '@lifeomic/phc-web-toolkit/src/components/EnforceABAC';
import urls from '../../util/urls';
import { withRouter, Link } from 'react-router-dom';
import {
  Popover,
  PopoverActions,
  PopoverList,
  PopoverItem,
} from '@lifeomic/chroma-react/components/Popover';
import {
  projectPickerActions,
  projectPickerSelectors,
} from '@lifeomic/phc-web-toolkit/src/redux/modules/projectPicker';
import { getTestProps } from '@lifeomic/phc-web-toolkit/src/util/testProps';
import { accountSelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/account';
import { evaluatedPolicySelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/evaluatedPolicy';
import { IconButtonLink } from '@lifeomic/chroma-react/components/IconButtonLink';
import { featuresSelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/features';

const messages = defineMessages({
  createAProject: {
    id: 'project.projectPicker.createAProject',
    defaultMessage: 'Create a project',
  },
});

const styles = theme => ({
  addIcon: {
    height: 20,
  },
  addProjectTitle: {
    paddingLeft: theme.spacing(1),
  },
  completeIcon: {
    color: theme.palette.primary.main,
    marginLeft: theme.spacing(2),
    height: 18,
    width: 18,
  },
  popover: {
    zIndex: 1500,
  },
  popoverItem: {
    overflow: 'hidden',
    position: 'relative',
    width: '15.5rem',
    '& p': {
      width: 'calc(100% - 20px)',
    },
    '&:hover': {
      '& $settings': {
        opacity: 1,
        right: 20,
        transition: '0.2s ease-out',
        transitionDelay: '0.3s',
      },
      '& $completeIcon': {
        opacity: 0,
        transition: 'opacity 0.3s ease',
      },
    },
  },
  seeAllLink: {
    width: '100%',
    '&:hover': {
      textDecoration: 'none',
    },
  },
  projectPickerProjectButton: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  settings: {
    fill: theme.palette.primary[50],
    opacity: 0,
    position: 'absolute',
    padding: 0,
    right: -20,
  },
});

const NUM_PROJECTS = 1000;

class ProjectPicker extends React.PureComponent {
  componentDidMount() {
    if (
      this.props.activeAccount &&
      (!_.get(this, 'props.project.id') ||
        _.get(this, 'props.projects', []).length === 0)
    ) {
      this.props.fetchProjects({
        params: {
          pageSize: NUM_PROJECTS,
        },
      });
    }
  }

  // https://reactjs.org/blog/2018/03/27/update-on-async-rendering.html
  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps(nextProps) {
    const currentActiveAcctId = _.get(this.props.activeAccount, 'id');
    const nextActiveAcctId = _.get(nextProps, 'activeAccount.id');
    if (
      (currentActiveAcctId || nextActiveAcctId) &&
      currentActiveAcctId !== nextActiveAcctId
    ) {
      this.props.fetchProjects({
        params: {
          pageSize: NUM_PROJECTS,
        },
      });
    }
  }

  handleSelect = (id, popover) => e => {
    const { features, push, location } = this.props;

    this.props.setActiveProject(id);
    popover.hide();

    if (features?.projectLandingPage && location.pathname === '/home') {
      push(urls.app.projectLandingPage(id));
    }
  };

  handleAddClick = e => {
    this.props.push(urls.app.projects());
  };

  handleSeeAllProjectsClick = popover => e => {
    popover.hide();
  };

  render() {
    const {
      classes,
      projects,
      activeProject,
      setActiveProject,
      policy,
      showLoader = true,
      intl: { formatMessage },
    } = this.props;
    const projectList = (projects.projectList || []).filter(
      project =>
        hasAccessToProject(policy, project) &&
        (!project.deletionDate || new Date(project.deletionDate) > Date.now()),
    );

    if (activeProject) {
      if (!projectList.find(p => p.id === activeProject.id)) {
        projectList.unshift(activeProject);
      }
    } else if (projectList.length > 0) {
      // Default the active project to the first project the user has access to:
      setActiveProject(projectList[0].id);
    }

    const hasMoreProjects =
      (this.props.projects.projectList || []).length > projectList.length;
    const activeProjectId = _.get(activeProject, 'id', '');
    const hasMenuItems = projectList.length > 0;

    if (projects.isFetching && showLoader) {
      return <DotLoader size={1} />;
    }

    if (projectList.length === 0) {
      return (
        <EnforceABAC permission={permissions.projectAdmin}>
          <Button
            onClick={this.handleAddClick}
            color="inverse"
            icon={PlusCircle}
            variant="text"
          >
            {formatMessage(messages.createAProject)}
          </Button>
        </EnforceABAC>
      );
    }

    return (
      <Popover
        aria-label={
          <FormattedMessage
            id="project.projectPicker.projectSwitcher"
            defaultMessage="Project switcher"
          />
        }
        className={classes.popover}
        placement="bottom-end"
        unstable_offset={[75, 0]}
        usePortal={true}
        title={
          <FormattedMessage
            id="project.projectPicker.projectsTitle"
            defaultMessage="Projects"
          />
        }
        anchorElement={
          <Button
            aria-label={
              <FormattedMessage
                id="project.projectPicker.openProjectPicker"
                defaultMessage="Open project picker"
              />
            }
            variant="text"
            color="inverse"
            className={classes.projectPickerProjectButton}
            trailingIcon={hasMenuItems && ChevronDown}
            {...getTestProps('sidebarnav-project-picker')}
          >
            {activeProject && <span>{activeProject.name}</span>}
            {!activeProject && (
              <FormattedMessage
                id="project.projectPicker.noActiveProject"
                defaultMessage="Projects {count}"
                values={{
                  count: `(${projectList.length}${hasMoreProjects ? '+' : ''})`,
                }}
              />
            )}
          </Button>
        }
      >
        {({ popover }) => (
          <React.Fragment>
            <PopoverList>
              {projectList.length > 0 &&
                projectList.map((project, i) => (
                  <PopoverItem
                    className={classes.popoverItem}
                    key={i}
                    noTextWrap={true}
                    clipText={true}
                    onClick={this.handleSelect(project.id, popover)}
                    text={project.name}
                    justify="space-between"
                    {...getTestProps(`project-popover-item-${project.id}`)}
                  >
                    {activeProjectId === project.id && (
                      <CheckCircle className={classes.completeIcon} />
                    )}
                    <EnforceABAC permission={permissions.projectAdmin}>
                      <IconButtonLink
                        aria-label={`${project.name} settings`}
                        icon={Settings}
                        size={0}
                        className={classes.settings}
                        to={urls.app.projectSettings(project.id)}
                        {...getTestProps(
                          `project-configuration-link-item-${project.id}`,
                        )}
                      />
                    </EnforceABAC>
                  </PopoverItem>
                ))}
            </PopoverList>
            <PopoverActions>
              <Link to={urls.app.projects()} className={classes.seeAllLink}>
                <Button
                  variant="outlined"
                  onClick={this.handleSeeAllProjectsClick(popover)}
                  fullWidth
                  {...getTestProps('project-picker-see-all')}
                >
                  <FormattedMessage
                    id="project.projectPicker.seeAll"
                    defaultMessage="See All"
                  />
                </Button>
              </Link>
            </PopoverActions>
          </React.Fragment>
        )}
      </Popover>
    );
  }
}

const mapStateToProps = state => ({
  activeProject: projectPickerSelectors.selectActiveProject(state),
  projects: projectPickerSelectors.selectProjectPickerState(state),
  activeAccount: accountSelectors.selectActiveAccount(state),
  policy: evaluatedPolicySelectors.selectPolicyForUser(state),
  features: featuresSelectors.selectFeatures(state),
});

const mapDispatchToProps = {
  push,
  fetchProjects: projectPickerActions.fetchProjects,
  setActiveProject: projectPickerActions.setActiveProject,
};

export default compose(
  injectIntl,
  withStyles(styles),
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
)(ProjectPicker);
